// Approval Workflow Agent - User Control for Critical Decisions
// frontend/src/agents/specialized/ApprovalWorkflowAgent.ts

import { BaseAgent } from '../core/BaseAgent';
import { AgentCommunicationHub } from '../core/AgentCommunicationHub';

export interface Decision {
    id: string;
    title: string;
    description: string;
    type: 'optimization' | 'evolution' | 'cost_increase' | 'design_change' | 'other';

    // Impact
    cost_delta: number;
    rpn_delta: number;
    risk_level: 'low' | 'medium' | 'high' | 'critical';
    criticality: 'trivial' | 'minor' | 'moderate' | 'major' | 'critical';

    // Budget context
    current_cost?: number;
    budget?: number;
    would_exceed_budget: boolean;

    // Details
    details: {
        what_changes: string;
        why_needed: string;
        alternatives?: string[];
        impacts?: string[];
    };

    // Source
    requested_by: string;
    timestamp: Date;

    // Approval
    requires_approval: boolean;
    auto_approved?: boolean;
    approval_priority: 'auto' | 'notify' | 'request' | 'mandatory';
}

export interface ApprovalRequest {
    decision: Decision;
    options: Array<{
        value: 'approve' | 'reject' | 'modify' | 'defer';
        label: string;
        description: string;
    }>;
    timeout?: number;  // ms before auto-decision
    default_action?: 'approve' | 'reject';
}

export interface ApprovalResponse {
    decision_id: string;
    choice: 'approve' | 'reject' | 'modify' | 'defer';
    reason?: string;
    modifications?: Record<string, any>;
    approved_by: 'user' | 'auto' | 'timeout';
    timestamp: Date;
}

export class ApprovalWorkflowAgent extends BaseAgent {
    // Thresholds for auto-approval
    autoApproveCostThreshold: number = 1.00;  // Auto-approve if < $1
    requestApprovalCostThreshold: number = 3.00;  // Request approval if > $3

    // Approval history
    approvalHistory: Array<{ request: ApprovalRequest; response: ApprovalResponse }> = [];

    // Pending approvals
    pendingApprovals: Map<string, ApprovalRequest> = new Map();

    constructor(commHub: AgentCommunicationHub) {
        super('ApprovalWorkflowAgent', commHub);
    }

    /**
     * Request approval for a decision
     */
    async requestApproval(decision: Decision): Promise<ApprovalResponse> {
        console.log(`[ApprovalWorkflowAgent] Decision: ${decision.title}`);

        // Calculate priority
        const priority = this.calculatePriority(decision);
        decision.approval_priority = priority;

        // Auto-approve if trivial
        if (priority === 'auto') {
            console.log(`[ApprovalWorkflowAgent] ✅ AUTO-APPROVED: ${decision.title} (trivial decision)`);

            const response: ApprovalResponse = {
                decision_id: decision.id,
                choice: 'approve',
                reason: 'Auto-approved (trivial)',
                approved_by: 'auto',
                timestamp: new Date()
            };

            this.recordApproval(decision, response);
            return response;
        }

        // Notify user but don't block
        if (priority === 'notify') {
            console.log(`[ApprovalWorkflowAgent] ℹ️ NOTIFYING: ${decision.title} (medium impact)`);

            this.broadcast({
                eventType: 'decision-notification',
                decision,
                autoApproved: true
            });

            const response: ApprovalResponse = {
                decision_id: decision.id,
                choice: 'approve',
                reason: 'Auto-approved with notification',
                approved_by: 'auto',
                timestamp: new Date()
            };

            this.recordApproval(decision, response);
            return response;
        }

        // Request user approval (blocking)
        if (priority === 'request' || priority === 'mandatory') {
            console.log(`[ApprovalWorkflowAgent] 🔔 REQUESTING APPROVAL: ${decision.title}`);

            const request: ApprovalRequest = {
                decision,
                options: [
                    {
                        value: 'approve',
                        label: 'Approve',
                        description: 'Accept this decision and continue'
                    },
                    {
                        value: 'reject',
                        label: 'Reject',
                        description: 'Reject this decision'
                    },
                    {
                        value: 'modify',
                        label: 'Modify',
                        description: 'Approve with modifications'
                    },
                    {
                        value: 'defer',
                        label: 'Defer',
                        description: 'Decide later'
                    }
                ],
                timeout: priority === 'request' ? 60000 : undefined,  // 60s timeout for 'request', none for 'mandatory'
                default_action: priority === 'request' ? 'approve' : undefined
            };

            // Store as pending
            this.pendingApprovals.set(decision.id, request);

            // Broadcast approval request to UI
            this.broadcast({
                eventType: 'approval-requested',
                request
            });

            // Wait for user response (in real implementation, this would be async/awaitable)
            // For now, we'll simulate with a placeholder
            const response = await this.waitForUserResponse(request);

            this.recordApproval(decision, response);
            return response;
        }

        // Default: reject
        const response: ApprovalResponse = {
            decision_id: decision.id,
            choice: 'reject',
            reason: 'Unknown priority level',
            approved_by: 'auto',
            timestamp: new Date()
        };

        return response;
    }

    /**
     * Calculate priority level for a decision
     */
    private calculatePriority(decision: Decision): 'auto' | 'notify' | 'request' | 'mandatory' {
        // MANDATORY: Budget violation
        if (decision.would_exceed_budget) {
            return 'mandatory';
        }

        // MANDATORY: Critical risk or criticality
        if (decision.risk_level === 'critical' || decision.criticality === 'critical') {
            return 'mandatory';
        }

        // REQUEST: High cost (> $3)
        if (Math.abs(decision.cost_delta) > this.requestApprovalCostThreshold) {
            return 'request';
        }

        // REQUEST: High risk or major criticality
        if (decision.risk_level === 'high' || decision.criticality === 'major') {
            return 'request';
        }

        // NOTIFY: Medium impact (cost $1-$3 OR moderate criticality)
        if (
            (Math.abs(decision.cost_delta) >= this.autoApproveCostThreshold &&
                Math.abs(decision.cost_delta) <= this.requestApprovalCostThreshold) ||
            decision.criticality === 'moderate'
        ) {
            return 'notify';
        }

        // AUTO: Low cost, low risk, trivial
        if (
            Math.abs(decision.cost_delta) < this.autoApproveCostThreshold &&
            decision.risk_level === 'low' &&
            decision.criticality === 'trivial'
        ) {
            return 'auto';
        }

        // Default: notify
        return 'notify';
    }

    /**
     * Wait for user response (simulated for now)
     * In real implementation, this would be event-driven
     */
    private async waitForUserResponse(request: ApprovalRequest): Promise<ApprovalResponse> {
        // This is a placeholder that would be replaced with actual UI integration
        // For now, auto-approve after timeout

        return new Promise((resolve) => {
            // In real implementation, this would listen for UI events
            // For now, simulate timeout
            const timeout = request.timeout || 60000;
            const defaultAction = request.default_action || 'reject';

            setTimeout(() => {
                resolve({
                    decision_id: request.decision.id,
                    choice: defaultAction,
                    reason: `Auto-${defaultAction}ed after timeout`,
                    approved_by: 'timeout',
                    timestamp: new Date()
                });
            }, timeout);
        });
    }

    /**
     * User approves a pending decision
     */
    async userApprove(
        decision_id: string,
        choice: 'approve' | 'reject' | 'modify' | 'defer',
        reason?: string,
        modifications?: Record<string, any>
    ): Promise<ApprovalResponse> {

        const request = this.pendingApprovals.get(decision_id);
        if (!request) {
            throw new Error(`No pending approval found for decision: ${decision_id}`);
        }

        const response: ApprovalResponse = {
            decision_id,
            choice,
            reason,
            modifications,
            approved_by: 'user',
            timestamp: new Date()
        };

        // Remove from pending
        this.pendingApprovals.delete(decision_id);

        // Record approval
        this.recordApproval(request.decision, response);

        // Broadcast response
        this.broadcast({
            eventType: 'approval-response',
            response
        });

        console.log(`[ApprovalWorkflowAgent] ${choice.toUpperCase()}: ${request.decision.title}`);

        return response;
    }

    /**
     * Record approval in history
     */
    private recordApproval(decision: Decision, response: ApprovalResponse): void {
        this.approvalHistory.push({
            request: {
                decision,
                options: []  // Omit options for history
            },
            response
        });

        this.broadcast({
            eventType: 'approval-recorded',
            decision: decision.id,
            choice: response.choice
        });
    }

    /**
     * Get approval statistics
     */
    getStatistics(): {
        total: number;
        autoApproved: number;
        userApproved: number;
        rejected: number;
        timeout: number;
        pending: number;
    } {
        const stats = {
            total: this.approvalHistory.length,
            autoApproved: 0,
            userApproved: 0,
            rejected: 0,
            timeout: 0,
            pending: this.pendingApprovals.size
        };

        for (const { response } of this.approvalHistory) {
            if (response.choice === 'reject') {
                stats.rejected++;
            } else if (response.approved_by === 'auto') {
                stats.autoApproved++;
            } else if (response.approved_by === 'user') {
                stats.userApproved++;
            } else if (response.approved_by === 'timeout') {
                stats.timeout++;
            }
        }

        return stats;
    }

    /**
     * Get approval history
     */
    getHistory(limit?: number): Array<{ request: ApprovalRequest; response: ApprovalResponse }> {
        if (limit) {
            return this.approvalHistory.slice(-limit);
        }
        return this.approvalHistory;
    }

    /**
     * Get pending approvals
     */
    getPendingApprovals(): ApprovalRequest[] {
        return Array.from(this.pendingApprovals.values());
    }
}
